import { useContext, useEffect } from "react"

//Formik helpers
import { useField } from "formik"
import FormikTextInput from "../../../helpers/formikInputs/FormikTextInput"
import FormikNumberInput from "../../../helpers/formikInputs/FormikNumberInput"
import FormikCheckboxInput from "../../../helpers/formikInputs/FormikCheckboxInput"
import FormikDropDown from "../../../helpers/formikInputs/FormikDropDown"
import FormikMultiSelectInput from "../../../helpers/formikInputs/FormikMultiSelectInput"

// Context
import { UserContext } from "../../../context/userContext"

//PrimeReact
import { Panel } from "primereact/panel"

//Styles
import myStyles from "../../../styles/assessments/ManagementForm.module.css"

export default function ManagementFormProfile({
  disabled,
  activeOrganization,
  organizations,
}) {
  const userCtx = useContext(UserContext)

  const [field] = useField("assessment.isSystemType")
  const [organizationIdsField, , organizationIdsHelper] =
    useField("organizationIds")

  const [creatingOrganizationField] = useField("assessment.organizationId")
  const [accessLevelField] = useField("assessment.accessLevel")
  const [idField] = useField("assessment.id")
  const [validateScoreField] = useField("assessment.validateScore")

  let organizationList = organizations || []
  let creatorId = creatingOrganizationField.value || activeOrganization

  //Ensure that the cretor is always shown as selected
  useEffect(() => {
    if (!organizationIdsField.value?.includes(creatorId)) {
      let newIds = organizationIdsField.value || []

      newIds = [creatorId, ...newIds]

      organizationIdsHelper.setValue(newIds)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //Ensure that the creator is shown as the owner
  const creatorEntry = organizationList.find(
    (organization) => organization.organization_id === creatorId
  )

  if (creatorEntry && !creatorEntry.organization_name?.includes(" [Owner]")) {
    let newCreatorEntry = {
      ...creatorEntry,
      organization_name: `${creatorEntry.organization_name} [Owner]`,
    }

    const creatorIndex = organizationList.indexOf(creatorEntry)

    organizationList[creatorIndex] = newCreatorEntry
  }

  const PanelHeader = () => {
    return (
      <div className={myStyles.configHeaderLayout}>
        <div className={myStyles.configHeaderLayoutLabel}>Configuration</div>
        <div
          className={`${myStyles.configHeaderLayoutActive} ${myStyles.switchControl}`}
        >
          <FormikCheckboxInput
            name={"assessment.enabled"}
            label={"Active"}
            disabled={disabled}
            className={`${myStyles.switchControlContainer}`}
            useSwitch={true}
          />
        </div>
      </div>
    )
  }

  return (
    <Panel header={PanelHeader} className={myStyles.configPanel}>
      <div className={myStyles.nameFieldText}>
        <FormikTextInput
          label={"Name"}
          name={"assessment.name"}
          required={true}
          placeholderText={"Assessment name"}
          disabled={disabled}
        />
      </div>

      <FormikTextInput
        label={"Description"}
        name={"assessment.description"}
        placeholderText={"Description"}
        disabled={disabled}
      />
      <div className={myStyles.configAccess}>
        <div className={myStyles.configAccessLevel}>
          <FormikDropDown
            name={"assessment.accessLevel"}
            label={"Access level"}
            options={[
              {
                label: "Private: Only available to creating organization",
                value: "private",
              },
              {
                label:
                  "Enterprise: Share with organizations within this enterprise",
                value: "enterprise",
              },
              {
                label:
                  "Public: Share with organizations within this and other enterprises",
                value: "public",
              },
            ]}
            onChangeCallback={(value) => {
              switch (value) {
                case "enterprise":
                  let newOrgList = organizations
                    ?.map((organization) => organization.organization_id)
                    ?.filter((org) => org !== creatorId)

                  newOrgList = newOrgList
                    ? [creatorId, ...newOrgList]
                    : [creatorId]

                  organizationIdsHelper.setValue(newOrgList)
                  break
                default:
                  organizationIdsHelper.setValue([creatorId])
                  break
              }
            }}
            helpText={""}
            disabled={disabled}
          />
        </div>
        <div className={myStyles.configAccessOrganizations}>
          <FormikMultiSelectInput
            name={"organizationIds"}
            label={"Organizations"}
            options={organizationList}
            optionLabel={"organization_name"}
            optionValue={"organization_id"}
            helpText={""}
            displayType={"comma"}
            maxSelectedLabels={3}
            disabled={disabled || accessLevelField.value !== "enterprise"}
            disableFormikSubmit={true} //This disables auto submission to formik
            onChangeCallBack={(value) => {
              //Ensure that the creating organization is always displayed as a selected organization
              if (!value.includes(creatorId)) {
                value = [creatorId, ...value]
              }

              organizationIdsHelper.setValue(value)
            }}
          />
        </div>
      </div>
      <div
        className={`${myStyles.validateScoreLayout} ${myStyles.switchControl}`}
      >
        <div className={`${myStyles.validateScoreLayoutValidate}`}>
          <FormikCheckboxInput
            name={"assessment.validateScore"}
            label={"Validate score"}
            helpText={""}
            disabled={disabled}
            className={` ${myStyles.configOptionItemBorder} ${myStyles.switchControlContainer}`}
            useSwitch={true}
          />
        </div>
        <div className={myStyles.validateScoreLayoutMinScore}>
          <FormikNumberInput
            name={"assessment.minScore"}
            label={"Minimum score"}
            disabled={disabled || !validateScoreField.value}
            minFractionDigits={0}
          />
        </div>
        <div className={myStyles.validateScoreLayoutMaxScore}>
          <FormikNumberInput
            name={"assessment.maxScore"}
            label={"Maximum score"}
            disabled={disabled || !validateScoreField.value}
            minFractionDigits={0}
          />
        </div>
      </div>

      <div className={`${myStyles.configOptions} ${myStyles.switchControl}`}>
        <FormikCheckboxInput
          name={"assessment.displayScore"}
          label={"Display score"}
          disabled={disabled}
          className={`${myStyles.configOptionsScore} ${myStyles.configOptionItemBorder} ${myStyles.switchControlContainer}`}
          useSwitch={true}
        />
        <FormikCheckboxInput
          name={"assessment.displayDisposition"}
          label={"Display final outcomes"}
          disabled={disabled}
          className={`${myStyles.configOptionsDisposition} ${myStyles.configOptionItemBorder} ${myStyles.switchControlContainer}`}
          useSwitch={true}
        />
        <FormikCheckboxInput
          name={"assessment.displayRemarks"}
          label={"Display remarks"}
          disabled={disabled}
          className={`${myStyles.configOptionsRemarks} ${myStyles.configOptionItemBorder} ${myStyles.switchControlContainer}`}
          useSwitch={true}
        />
        <FormikCheckboxInput
          name={"assessment.displayQuestions"}
          label={"Display questions"}
          disabled={disabled}
          className={`${myStyles.configOptionsQuestions} ${myStyles.configOptionItemBorder} ${myStyles.switchControlContainer}`}
          useSwitch={true}
        />
        <FormikCheckboxInput
          name={"assessment.displayAnswers"}
          label={"Display answers"}
          disabled={disabled}
          className={`${myStyles.configOptionsAnswers} ${myStyles.configOptionItemBorder} ${myStyles.switchControlContainer}`}
          useSwitch={true}
        />
        <FormikCheckboxInput
          name={"assessment.displayAnswerValues"}
          label={"Display answer values"}
          disabled={disabled}
          className={`${myStyles.configOptionsAnswerValues} ${myStyles.configOptionItemBorder} ${myStyles.switchControlContainer}`}
          useSwitch={true}
        />
        {userCtx.session.user.staff_role === 1 ? (
          <>
            <FormikCheckboxInput
              name={"assessment.isSystemType"}
              label={"System assessment"}
              disabled={disabled || idField.value !== undefined}
              className={`${myStyles.configOptionsSystem} ${myStyles.configOptionItemBorder} ${myStyles.switchControlContainer}`}
              useSwitch={true}
            />
            <div className={myStyles.configOptionsSystemCode}>
              <span className={myStyles.systemCodeLabel}>
                System assessment code:
              </span>

              <div className={myStyles.systemCodeInput}>
                <FormikTextInput
                  name={"assessment.systemTypeCode"}
                  placeholderText={"Code"}
                  disabled={disabled || !field.value}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Panel>
  )
}
